import { Link } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { DeltaBadge } from './DeltaBadge';
import { Sparkline } from './Sparkline';
import { EmptyState } from './EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import type { OutletRanking } from '@/hooks/useOutletRanking';

interface Props {
  rankings: OutletRanking[];
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  format: (n: number) => string;
  /** Show ↑ or ↓ chevron next to each row */
  tone?: 'drop' | 'spike' | 'movers';
}

export function MoversList({
  rankings,
  loading,
  emptyTitle = 'No movers',
  emptyDescription = 'No outlets crossed the comparison threshold in this period.',
  format,
  tone = 'movers',
}: Props) {
  if (loading) {
    return (
      <ul className="divide-y divide-border/60">
        {[0, 1, 2].map((i) => (
          <li key={i} className="flex items-center gap-3 py-3">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="ml-auto h-6 w-16" />
          </li>
        ))}
      </ul>
    );
  }
  if (rankings.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }
  return (
    <ul className="divide-y divide-border/60">
      {rankings.map((r) => {
        const Icon =
          tone === 'drop' ? ArrowDownRight : tone === 'spike' ? ArrowUpRight : null;
        const iconClass =
          tone === 'drop' ? 'text-negative' : tone === 'spike' ? 'text-positive' : '';
        return (
          <li key={r.outletId} className="flex items-center gap-3 py-3">
            {Icon && <Icon className={`h-4 w-4 shrink-0 ${iconClass}`} />}
            <Link
              to={`/outlets/${r.outletId}`}
              className="min-w-0 flex-1 truncate text-sm font-medium hover:underline"
            >
              {r.outletName}
              <span className="ml-2 text-2xs text-muted-foreground">{r.city}</span>
            </Link>
            {r.trend.length > 1 && (
              <Sparkline
                data={r.trend}
                width={80}
                height={28}
                tone={tone === 'drop' ? 'negative' : tone === 'spike' ? 'positive' : 'neutral'}
                className="shrink-0"
              />
            )}
            <div className="flex shrink-0 flex-col items-end gap-0.5">
              <DeltaBadge
                deltaPct={r.metric.deltaPct}
                comparable={r.metric.comparable}
                size="sm"
              />
              <span className="font-mono text-2xs text-muted-foreground">
                {format(r.metric.current)}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
